/**
 * LivePreviewCanvas — React wrapper that hosts the PixiJS Application for the Live Preview.
 * Mirrors the PixiSceneCanvas pattern: PixiAppManager manages the Application lifecycle,
 * LivePreviewRenderer manages the imperative display-object tree.
 */
import React, { useRef, useEffect, useMemo, useState } from 'react';
import { PixiAppManager } from '@renderer/pixi/PixiAppManager';
import { LivePreviewRenderer } from './pixi/LivePreviewRenderer';
import { useLivePreviewStore } from '@store/livePreviewStore';
import { useSceneStore } from '@store/sceneStore';
import { useSlotGameRuntimeStore } from '@store/slotGameRuntimeStore';
import { cn } from '../../utils/cn';
import type { CascadeSlotConfig } from '@core/types/cascade-slot.types';
import { DEFAULT_CASCADE_SLOT_CONFIG, CascadeGamePhase as Phase } from '@core/types/cascade-slot.types';
import type { BackendGameComponentData } from '@core/types/backend-game.types';
import { convertBackendToSlotConfig } from '../../services/backend-game/ConfigConverter';

interface LivePreviewCanvasProps {
  className?: string;
}

export const LivePreviewCanvas: React.FC<LivePreviewCanvasProps> = ({ className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const appManagerRef = useRef<PixiAppManager | null>(null);
  const rendererRef = useRef<LivePreviewRenderer | null>(null);
  const [, setContainerSize] = useState({ width: 0, height: 0 });

  const viewport = useLivePreviewStore((s) => s.viewport);
  const playState = useLivePreviewStore((s) => s.playState);
  const gameObjects = useSceneStore((s) => s.gameObjects);

  // ── Derive slot game config from scene ──────────────────────────
  const { cascadeConfig, hasCascadeSlot, symbolTextures } = useMemo(() => {
    const objects = Object.values(gameObjects);

    // Find backend-slot-game or cascade-slot root
    const backendObj = objects.find((go) =>
      go.components.some((c) => c.type === 'backend-game-config'),
    );
    const cascadeObj = objects.find((go) =>
      go.components.some((c) => c.type === 'cascade-slot-config'),
    );

    let config: CascadeSlotConfig | null = null;
    let textures: Record<string, string> = {};

    if (backendObj) {
      const comp = backendObj.components.find(
        (c) => c.type === 'backend-game-config',
      );
      const data = comp?.data as BackendGameComponentData | undefined;
      if (data?.backendConfig) {
        try {
          config = convertBackendToSlotConfig(data);
          textures = data.symbolTextures || {};
        } catch {
          config = { ...DEFAULT_CASCADE_SLOT_CONFIG };
        }
      }
    } else if (cascadeObj) {
      const comp = cascadeObj.components.find(
        (c) => c.type === 'cascade-slot-config',
      );
      config = {
        ...DEFAULT_CASCADE_SLOT_CONFIG,
        ...comp?.data,
      } as CascadeSlotConfig;
    }

    return {
      cascadeConfig: config,
      hasCascadeSlot: !!config,
      symbolTextures: textures,
    };
  }, [gameObjects]);

  // Initialize PixiJS Application
  useEffect(() => {
    if (!containerRef.current) return;

    const appManager = new PixiAppManager();
    appManagerRef.current = appManager;

    let mounted = true;

    const init = async () => {
      try {
        const app = await appManager.init(
          containerRef.current!,
          containerRef.current!.clientWidth || 400,
          containerRef.current!.clientHeight || 600,
          {
            backgroundAlpha: 1,
            backgroundColor: 0x1a1a2e,
          }
        );

        if (!mounted) {
          appManager.destroy();
          return;
        }

        const renderer = new LivePreviewRenderer(app);
        rendererRef.current = renderer;
        renderer.updateScaling();
      } catch (err) {
        console.error('[LivePreviewCanvas] Init failed:', err);
      }
    };

    init();

    return () => {
      mounted = false;
      rendererRef.current?.setPlayMode(false, null, {});
      rendererRef.current?.destroy();
      rendererRef.current = null;
      appManager.destroy();
      appManagerRef.current = null;
    };
  }, []);

  // Handle container resize via ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
        appManagerRef.current?.resize(width, height);
        rendererRef.current?.resize(width, height);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Update scaling when viewport settings change
  useEffect(() => {
    rendererRef.current?.updateScaling();
  }, [viewport.customWidth, viewport.customHeight, viewport.scale, viewport.fitToPanel]);

  // ── Play mode wiring ────────────────────────────────────────────
  useEffect(() => {
    if (!rendererRef.current) return;

    if (playState === 'playing' && hasCascadeSlot && cascadeConfig) {
      rendererRef.current.setPlayMode(true, cascadeConfig, symbolTextures);

      // Wire button interactivity
      const objects = useSceneStore.getState().gameObjects;

      const findByName = (name: string) =>
        Object.values(objects).find((go) => go.name === name);

      // Spin button — cycles spin / force-stop / stop-auto
      const spinBtn = findByName('spin-button');
      if (spinBtn) {
        rendererRef.current.makeButtonInteractive(spinBtn.id, () => {
          const {
            gamePhase,
            requestSpin,
            forceStop,
            autoSpinActive,
            stopAutoSpin,
          } = useSlotGameRuntimeStore.getState();
          if (autoSpinActive) {
            stopAutoSpin();
          } else if (gamePhase === Phase.Idle) {
            requestSpin();
          } else {
            forceStop();
          }
        });
      }

      // Bet button — cycle through bet levels
      const betBtn = findByName('bet-option-button');
      if (betBtn) {
        rendererRef.current.makeButtonInteractive(betBtn.id, () => {
          const { currentBet, setBetLevel } =
            useSlotGameRuntimeStore.getState();
          const levels = cascadeConfig.betLevels || [
            100, 500, 1000, 5000, 10000,
          ];
          const currentIdx = levels.indexOf(currentBet);
          const nextIdx = (currentIdx + 1) % levels.length;
          setBetLevel(levels[nextIdx]);
        });
      }

      // Spin-option button — toggle turbo
      const spinOptBtn = findByName('spin-option-button');
      if (spinOptBtn) {
        rendererRef.current.makeButtonInteractive(spinOptBtn.id, () => {
          useSlotGameRuntimeStore.getState().toggleTurbo();
        });
      }

      // Setting button — toggle main/second panel positions
      const settingBtn = findByName('setting-button');
      if (settingBtn) {
        let showingMain = true;
        rendererRef.current.makeButtonInteractive(settingBtn.id, () => {
          const objs = useSceneStore.getState().gameObjects;
          const mainPanel = Object.values(objs).find(
            (go) => go.name === 'main-panel',
          );
          const secondPanel = Object.values(objs).find(
            (go) => go.name === 'second-panel',
          );
          if (mainPanel && secondPanel) {
            const { updateTransform } = useSceneStore.getState();
            if (showingMain) {
              updateTransform(mainPanel.id, {
                position: { x: 0, y: 250 },
              });
              updateTransform(secondPanel.id, {
                position: { x: 0, y: -250 },
              });
            } else {
              updateTransform(mainPanel.id, {
                position: { x: 0, y: -250 },
              });
              updateTransform(secondPanel.id, {
                position: { x: 0, y: 250 },
              });
            }
            showingMain = !showingMain;
          }
        });
      }
    } else {
      rendererRef.current.setPlayMode(false, null, {});
    }
  }, [playState, hasCascadeSlot, cascadeConfig, symbolTextures]);

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full h-full overflow-hidden', className)}
    >
      {/* PixiJS canvas is appended here by PixiAppManager */}
    </div>
  );
};
